/**
 * TV1-1933 (S18) — pure data-shaping for the Critical-Stock list widget.
 *
 * The widget is Cube-backed: `getWidgetData` runs the CriticalStock cube query
 * and hands `resultSet.rawData()` straight here. Cube returns member-qualified
 * keys (`CriticalStock.sku`, `CriticalStock.currentStock`, …) and numeric
 * measures/dimensions as STRINGS (booleans too — `'true'` / `'false'`), so this
 * function normalizes each row to a typed {@link CriticalStockRow}.
 *
 * TV1-2392 (slice 6) — REWRITTEN. There is no client-side threshold any more.
 * `DEFAULT_CRITICAL_STOCK_THRESHOLD` and the `0 < runway < N` cut are GONE —
 * "crítico" is now `CriticalStock.belowThreshold`, a boolean computed **entirely
 * server-side** (item beats warehouse beats account umbral, resolved at read
 * time — see `CriticalStock.js`). This function only SELECTS rows where that
 * server-computed predicate is `true`; it never re-derives criticality from
 * `daysOfRunway`, which survives only as a labelled DISPLAY column.
 *
 * ⚠️ The server predicate is a DIFFERENT METRIC, not a superset of the old cut.
 * `0 < runway < 14` measured days of cover; `belowThreshold` measures units under a
 * seller-declared umbral. Neither contains the other. A fast-draining SKU with high
 * absolute stock (400 units, 40/day, 10 days of cover) WAS flagged by the old cut and
 * is deliberately NOT flagged now — that is D4's accepted trade (runway thresholds
 * need a scheduled evaluation, which is out of scope), not an equivalence.
 *
 * The list widget's own query (`curatedReports.ts`) deliberately does NOT apply
 * a Cube query-level filter for `belowThreshold` — a query-level `WHERE` would
 * also gate `thresholdUnits`, making it impossible to tell "no rows are
 * critical" apart from "some rows never resolved an umbral at all". Fetching
 * every (sku, warehouse) row unfiltered and selecting here keeps BOTH signals
 * available from the one round trip: {@link shapeCriticalStockRows} returns the
 * critical rows AND how many rows resolved no umbral — the partial-coverage
 * disclosure this list shares with the KPI (Risk #4's side door).
 *
 * `daysOfRunway = 0` — the (sku, warehouse) is genuinely STOCKED OUT — is now
 * VISIBLE. The previous version's `daysOfRunway > 0` cut silently dropped the
 * single most critical row on the list (TV1-2392 slice 1's carried-forward
 * defect); nothing here re-introduces that cut.
 */

export type CriticalStockRow = {
  sku: string;
  warehouseId: string;
  currentStock: number;
  dailyVelocity: number;
  /** `null` when the (sku, warehouse) had ZERO outflow in the trailing 30 days —
   * there is no runway to express. NEVER `0`: see the recompute site below. */
  daysOfRunway: number | null;
  /** The seller-defined umbral this row was measured against. */
  thresholdUnits: number;
  /** How far under the umbral — the list's sort key. */
  unitsBelowThreshold: number;
};

export type ShapeCriticalStockResult = {
  rows: CriticalStockRow[];
  /**
   * `true` when AT LEAST ONE (sku, warehouse) row in the unfiltered fetch had no
   * umbral to resolve against — the mixed-coverage case: some rows resolve,
   * others don't, so a naive read of the critical rows alone would show a
   * confident small number while the unresolved rows stay invisible.
   */
  partialCoverage: boolean;
};

export type ShapeCriticalStockOptions = {
  /** Cube member for the SKU dimension. */
  skuField?: string;
  /** Cube member for the warehouse dimension. */
  warehouseIdField?: string;
  /** Cube member for current stock. */
  currentStockField?: string;
  /** Cube member for the per-day velocity. */
  dailyVelocityField?: string;
  /** Cube member for the resolved umbral, in units. NULL if unresolved. */
  thresholdUnitsField?: string;
  /** Cube member for the server-computed crítico boolean. NULL if unresolved. */
  belowThresholdField?: string;
  /** Cube member for units-below-threshold — the sort key. NULL unless crítico. */
  unitsBelowThresholdField?: string;
};

const DEFAULT_SKU_FIELD = 'CriticalStock.sku';
const DEFAULT_WAREHOUSE_ID_FIELD = 'CriticalStock.warehouseId';
const DEFAULT_CURRENT_STOCK_FIELD = 'CriticalStock.currentStock';
const DEFAULT_DAILY_VELOCITY_FIELD = 'CriticalStock.dailyVelocity';
const DEFAULT_THRESHOLD_UNITS_FIELD = 'CriticalStock.thresholdUnits';
const DEFAULT_BELOW_THRESHOLD_FIELD = 'CriticalStock.belowThreshold';
const DEFAULT_UNITS_BELOW_THRESHOLD_FIELD = 'CriticalStock.unitsBelowThreshold';

/**
 * Resolve a Cube member value off a raw row. Cube keys are member-qualified
 * (`Cube.field`); match the exact key first, then fall back to the last segment
 * so the shaping survives a cube rename or an unprefixed column.
 */
const readMember = ( row: Record<string, unknown>, field: string ): unknown => {
  if ( field in row ) return row[field];
  const suffix = field.includes( '.' ) ? field.slice( field.lastIndexOf( '.' ) + 1 ) : field;
  const key = Object.keys( row ).find(
    candidate => candidate === suffix || candidate.endsWith( `.${suffix}` )
  );
  return key !== undefined ? row[key] : undefined;
};

const toNumber = ( value: unknown ): number => {
  if ( typeof value === 'number' ) return value;
  if ( typeof value === 'string' && value.trim() !== '' ) return Number( value );
  return NaN;
};

/** Cube ships booleans over the wire as the strings `'true'` / `'false'`. */
const toBoolean = ( value: unknown ): boolean | null => {
  if ( typeof value === 'boolean' ) return value;
  if ( value === 'true' ) return true;
  if ( value === 'false' ) return false;
  return null;
};

/** `null`/`undefined`/`''` all mean "unresolved" — never coerce to a number. */
const isUnresolved = ( value: unknown ): boolean =>
  value === null || value === undefined || value === '';

export const shapeCriticalStockRows = (
  rawData: unknown,
  options: ShapeCriticalStockOptions = {}
): ShapeCriticalStockResult => {
  if ( !Array.isArray( rawData )) return { rows: [], partialCoverage: false };

  const skuField = options.skuField ?? DEFAULT_SKU_FIELD;
  const warehouseIdField = options.warehouseIdField ?? DEFAULT_WAREHOUSE_ID_FIELD;
  const currentStockField = options.currentStockField ?? DEFAULT_CURRENT_STOCK_FIELD;
  const dailyVelocityField = options.dailyVelocityField ?? DEFAULT_DAILY_VELOCITY_FIELD;
  const thresholdUnitsField = options.thresholdUnitsField ?? DEFAULT_THRESHOLD_UNITS_FIELD;
  const belowThresholdField = options.belowThresholdField ?? DEFAULT_BELOW_THRESHOLD_FIELD;
  const unitsBelowThresholdField = options.unitsBelowThresholdField ?? DEFAULT_UNITS_BELOW_THRESHOLD_FIELD;

  let partialCoverage = false;
  const rows: CriticalStockRow[] = [];

  for ( const raw of rawData ) {
    if ( typeof raw !== 'object' || raw === null ) continue;
    const row = raw as Record<string, unknown>;

    const thresholdRaw = readMember( row, thresholdUnitsField );
    if ( isUnresolved( thresholdRaw )) {
      // No umbral resolved for this (sku, warehouse) — counts toward the
      // partial-coverage disclosure, never toward the list itself.
      partialCoverage = true;
      continue;
    }

    const belowThreshold = toBoolean( readMember( row, belowThresholdField ));
    if ( belowThreshold !== true ) continue;

    const sku = readMember( row, skuField );
    const warehouseId = readMember( row, warehouseIdField );
    const currentStock = toNumber( readMember( row, currentStockField ));
    const dailyVelocity = toNumber( readMember( row, dailyVelocityField ));
    const thresholdUnits = toNumber( thresholdRaw );
    const unitsBelowThreshold = toNumber( readMember( row, unitsBelowThresholdField ));

    if (
      typeof sku !== 'string' ||
      typeof warehouseId !== 'string' ||
      Number.isNaN( currentStock ) ||
      Number.isNaN( dailyVelocity ) ||
      Number.isNaN( thresholdUnits )
    ) {
      continue;
    }

    // `daysOfRunway` is a DISPLAY column only — never a predicate. Recomputed
    // rather than trusted off the wire so the widget stays the single authority
    // on the runway formula.
    //
    // ⚠️ `dailyVelocity` CAN be 0 (TV1-2392 cross-slice FIX 4). The cube's
    // `HAVING SUM(outflow) > 0` is gone: a (sku, warehouse) that has not moved in
    // 30 days now gets a row, because dead stock below its umbral IS crítico.
    // Zero velocity therefore yields `null` — "no hay cobertura que calcular" —
    // and NEVER `0`, which would render as "runs out today" in the critical band
    // for the safest-moving rows on the list. `daysOfRunway = 0` from a REAL
    // division (genuinely stocked out, `currentStock === 0`) is a distinct,
    // legitimate value and is still rendered.
    const daysOfRunway = dailyVelocity > 0 ? currentStock / dailyVelocity : null;

    rows.push({
      sku,
      warehouseId,
      currentStock,
      dailyVelocity,
      daysOfRunway,
      thresholdUnits,
      unitsBelowThreshold: Number.isNaN( unitsBelowThreshold ) ? 0 : unitsBelowThreshold
    });
  }

  // Sorted by units below threshold — the same quantity the KPI's predicate
  // counts. Cube's own `orderBy` already sorts the fetch; re-sorting here keeps
  // this function's output correct independent of the query that produced it.
  rows.sort(( a, b ) => b.unitsBelowThreshold - a.unitsBelowThreshold );

  return { rows, partialCoverage };
};
